import { useMutation, useQuery } from "@apollo/client"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from "react"
import { GET_CART } from "../../graphql/queries/cart/getCart"
import { CLEAR_CART } from "../../graphql/mutations/cart/clearCart"
import { randomString } from "../../helpers/randomString"
import { toast } from "react-toastify"
export const cartContext = createContext({})

export const ADD_MESSAGE = "add-message"
export const REMOVE_MESSAGE = "remove-message"
export const CART_ITEM_LOADING = "cart-loading"
export const CART_TOTAL_LOADING = "cart-total-loading"
export const CART_UPDATABLE = "cart-updatable"
export const CART_IS_CART_EMPTY = "cart-is-empty"
export const UPDATE_DATA_USER = "update-data-user"
const initState = {
  messages: [],
  cartItemLoading: false,
  cartTotalLoading: false,
  isCartEmpty: false,
  updatable: false,
  dataUser: {}
}
function reducer(state, action) {
  switch (action.type) {
    case ADD_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload]
      }
    case REMOVE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter((_, i) => i !== action.payload)
      }
    case CART_ITEM_LOADING:
      return {
        ...state,
        cartItemLoading: action.payload
      }
    case CART_TOTAL_LOADING:
      return {
        ...state,
        isCartEmpty: false,
        cartTotalLoading: action.payload
      }
    case CART_UPDATABLE:
      return {
        ...state,
        isCartEmpty: false,
        updatable: action.payload
      }
    case CART_IS_CART_EMPTY:
      return {
        ...state,
        isCartEmpty: action.payload
      }
    case UPDATE_DATA_USER:
      return {
        ...state,
        dataUser: action.payload
      }
    default:
      return state
  }
}
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initState)
  const [cartItems, setCartItems] = useState([])
  const [cartDetail, setCartDetail] = useState()
  const [valuePayment, setValuePayment] = useState("")
  const [shippingChosen, setShippingChosen] = useState("")
  const [check, setCheck] = useState(false)
  const [isAddToCart, setIsAddToCart] = useState(false)
  // Kept in a ref so `syncCartState` never reads a stale value from its closure.
  const isAddToCartRef = useRef(false)
  useEffect(() => {
    isAddToCartRef.current = isAddToCart
  }, [isAddToCart])

  // Single place where the server cart is copied into local state. Every cart
  // read (initial query, refetch after a mutation) must go through here:
  // `onCompleted` alone does not fire reliably on refetch, which left the UI
  // rendering the pre-mutation cart (e.g. an applied coupon not showing up
  // until a full page reload).
  const syncCartState = useCallback(cart => {
    setCartDetail(cart)
    setCartItems(Array.isArray(cart?.contents?.nodes) ? cart.contents.nodes : [])
    const chosenShipping =
      Array.isArray(cart?.chosenShippingMethods) && cart.chosenShippingMethods.length > 0
        ? cart.chosenShippingMethods[0]
        : ""
    if (chosenShipping) {
      setShippingChosen(chosenShipping)
    } else if (!cart || cart.isEmpty) {
      setShippingChosen("")
    }
    if (isAddToCartRef.current) {
      toast.success("Add to cart success!")
      isAddToCartRef.current = false
      setIsAddToCart(false)
    }
  }, [])

  const { loading, error, data, refetch } = useQuery(GET_CART, {
    onCompleted: data => syncCartState(data?.cart),
    onError: err => console.error(err),
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true
  })

  // Safety net: any query result that does not trigger `onCompleted` still
  // reaches local state here.
  useEffect(() => {
    if (data) {
      syncCartState(data.cart)
    }
  }, [data, syncCartState])

  // Use this instead of `refetch()` after any cart mutation: it waits for the
  // fresh cart and pushes it into local state before resolving.
  const refreshCart = useCallback(async () => {
    try {
      const { data: refreshed } = await refetch()
      syncCartState(refreshed?.cart)
      return refreshed?.cart
    } catch (err) {
      console.error(err)
      return null
    }
  }, [refetch, syncCartState])
  const [clearCart, { loading: clearLoading }] = useMutation(CLEAR_CART, {
    variables: {
      input: {
        clearPersistentCart: false,
        clientMutationId: randomString()
      }
    },
    onError: err => toast.error(err.message)
  })

  useEffect(() => {
    refreshCart()
  }, [])
  return (
    <cartContext.Provider
      value={{
        state,
        dispatch,
        refetch,
        refreshCart,
        cartItems,
        setCartItems,
        error,
        loading,
        cartDetail,
        check,
        setCheck,
        clearCart,
        clearLoading,
        valuePayment,
        setValuePayment,
        shippingChosen,
        setShippingChosen,
        isAddToCart,
        setIsAddToCart
      }}
    >
      {children}
    </cartContext.Provider>
  )
}

export const useCartContext = () => {
  return useContext(cartContext)
}

export const useCartDispatch = () => {
  const { dispatch } = useCartContext()
  return dispatch
}
